import React, { useState, useEffect, useMemo } from 'react';
import '../../styles/dash/Roles.css';
import EditRoleNameModal from './EditRoleNameModal';
import { getRoles, RolePermission } from '../../services/dashboard/GetRoles';
import LoadingSpinner from '../utility/LoadingSpinner';
import { getModulos, Modulos } from '../../services/dashboard/GetModulos';
import ModulesModal from './ModulesModal';
import { UpdNameRole } from '../../services/dashboard/UpdateRoleName';
import { ChangeStatusRol } from '../../services/dashboard/ChangeStatusRoles';
import CreateRoleModal from "./CreateRoleModal";
import { useNavigate } from 'react-router-dom';
import {
    useReactTable,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    flexRender,
    ColumnDef,
} from '@tanstack/react-table';

interface Role {
    id: number;
    name: string;
    status: number;
}

interface infoModule {
    action: string;
    actionID: number;
}

interface GroupedModulo {
    modulo: string;
    actions: infoModule[];
}

interface Permission {
    Id: number;
    IdAction: number;
    Action: string;
    IdModule: number;
    ModuleName: string;
}

interface Rolee {
    Id: number;
    RoleName: string;
    Permissions: Permission[];
}

interface RolePermissionWithId extends RolePermission {
    actionId: number;
}

// Solución: Usar React.ReactElement en lugar de JSX.Element
const Roles: React.FC = () => {
    const navigate = useNavigate();
    const [showEditModal, setShowEditModal] = useState(false);
    const [editingRole, setEditingRole] = useState<Role | null>(null);
    const [roles, setRoles] = useState<Role[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [permisos, setPermisos] = useState<number[]>([]);
    const [showModulesModal, setShowModulesModal] = useState(false);
    const [selectedRoleModules, setSelectedRoleModules] = useState<GroupedModulo[]>([]);
    const [allModulos, setAllModulos] = useState<Modulos[]>([]);
    const [rolePermissions, setRolePermissions] = useState<RolePermissionWithId[]>([]);
    const [idRol, setIdRol] = useState<number | null>(null);
    const [showNewRoleModal, setShowNewRoleModal] = useState(false);
    const [globalFilter, setGlobalFilter] = useState("");

    const fetchRoles = async () => {
        try {
            setIsLoading(true);
            const [rolePermissionsData, modulos] = await Promise.all([
                getRoles(),
                getModulos()
            ]);

            const permissionsWithId: RolePermissionWithId[] = rolePermissionsData.map(rp => ({
                ...rp,
                actionId: rp.actionId
            }));

            setRolePermissions(permissionsWithId);
            setAllModulos(modulos);

            const uniqueRoles = Array.from(
                new Map(
                    permissionsWithId.map(rp => [rp.idRol, {
                        id: rp.idRol,
                        name: rp.roleName,
                        status: rp.statusRole
                    }])
                ).values()
            );
            setRoles(uniqueRoles);
        } catch (err) {
            console.error('Error fetching roles:', err);
            setError('Error al cargar los roles');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        let hasPermission = false;
        const userData = localStorage.getItem("user");
        if (userData) {
            try {
                const userObject = JSON.parse(userData);
                const userDataParsed = JSON.parse(userObject.userData || "{}");

                if (userDataParsed?.roles?.length) {
                    const permisosAplanados = userDataParsed.roles.flatMap((rol: Rolee) =>
                        rol.Permissions.map((p: Permission) => p.IdAction)
                    );
                    setPermisos(permisosAplanados);
                    if (permisosAplanados.includes(18)) {
                        hasPermission = true;
                    }
                }
            } catch (err) {
                console.error('Error parsing user data:', err);
            }
        }
        if (!hasPermission) {
            navigate('/login');
        } else {
            fetchRoles();
        }
    }, [navigate]);

    const handleViewModules = (id: number) => {
        // Restaurar la lógica original usando allModulos
        const grouped: GroupedModulo[] = [];

        allModulos.forEach((mod) => {
            const existing = grouped.find(g => g.modulo === mod.moduloName);
            const actionInfo: infoModule = {
                action: mod.action,
                actionID: mod.actionID
            };

            if (existing) {
                const actionExists = existing.actions.some(a => a.actionID === mod.actionID);
                if (!actionExists) {
                    existing.actions.push(actionInfo);
                }
            } else {
                grouped.push({
                    modulo: mod.moduloName,
                    actions: [actionInfo]
                });
            }
        });

        setIdRol(id);
        setSelectedRoleModules(grouped);
        setShowModulesModal(true);
    };

    const handleOpenEditModal = (role: Role) => {
        setEditingRole(role);
        setShowEditModal(true);
    };

    const handleCloseEditModal = () => {
        setShowEditModal(false);
        setEditingRole(null);
    };

    const handleSaveRoleName = async (newName: string) => {
        if (!editingRole) return;

        setIsLoading(true);

        try {
            const resp = await UpdNameRole(editingRole.id, newName);

            if (resp.status === 200) {
                setRoles(prevRoles =>
                    prevRoles.map(role =>
                        role.id === editingRole.id ? { ...role, name: newName } : role
                    )
                );
                handleCloseEditModal();
                setIsLoading(false);
            } else {
                setIsLoading(false);
            }
        } catch (error) {
            setIsLoading(false);
            console.error("Error updating role name:", error);
            setError('Error al actualizar el nombre del rol');
        }
    };

    const handleToggleStatus = async (id: number) => {
        setIsLoading(true);

        try {
            const resp = await ChangeStatusRol(id);

            if (resp.status === 200) {
                setRoles(prevRoles =>
                    prevRoles.map(role =>
                        role.id === id ? { ...role, status: role.status === 1 ? 0 : 1 } : role
                    )
                );
                setIsLoading(false);
            }
        } catch (error) {
            setIsLoading(false);
            console.error('Error toggling status:', error);
        }
    };

    const handleBatchPermissionUpdate = (changes: {
        idRol: number;
        actionId: number | null;
        nuevoEstado: number
    }[]) => {
        const validChanges = changes.filter(change => change.actionId !== null) as {
            idRol: number;
            actionId: number;
            nuevoEstado: number;
        }[];

        setRolePermissions(prev => {
            const updatedPermissions = [...prev];

            validChanges.forEach(change => {
                const existingIndex = updatedPermissions.findIndex(
                    p => p.idRol === change.idRol && p.actionId === change.actionId
                );

                if (existingIndex !== -1) {
                    updatedPermissions[existingIndex] = {
                        ...updatedPermissions[existingIndex],
                        tienePermiso: change.nuevoEstado
                    };
                } else {
                    const basePermission = rolePermissions.find(rp =>
                        rp.idRol === change.idRol
                    );

                    const newPermission: RolePermissionWithId = {
                        ...(basePermission || {
                            idRol: change.idRol,
                            roleName: roles.find(r => r.id === change.idRol)?.name || "",
                            statusRole: 1,
                            action: "Nueva acción",
                            actionId: change.actionId,
                            tienePermiso: change.nuevoEstado
                        }),
                        actionId: change.actionId,
                        tienePermiso: change.nuevoEstado
                    };

                    updatedPermissions.push(newPermission);
                }
            });

            return updatedPermissions;
        });
    };

    function getPaginationRange(current: number, total: number): (number | string)[] {
        const delta = 1;
        const range: (number | string)[] = [];
        const rangeWithDots: (number | string)[] = [];
        let l = 0;

        for (let i = 1; i <= total; i++) {
            if (i === 1 || i === total || (i >= current - delta && i <= current + delta)) {
                range.push(i);
            }
        }

        for (let i of range) {
            if (l > 0) {
                if (Number(i) - l === 2) {
                    rangeWithDots.push(l + 1);
                } else if (Number(i) - l !== 1) {
                    rangeWithDots.push("...");
                }
            }
            rangeWithDots.push(i);
            l = Number(i);
        }

        return rangeWithDots;
    }


    const handleSuccess = () => {
        fetchRoles();
    };

    // Definición de columnas para react-table
    const columns = useMemo<ColumnDef<Role>[]>(() => [
        {
            id: 'actions',
            header: 'Opciones',
            cell: ({ row }) => {
                const role = row.original;
                const powerButtonColorClass = role.status === 1 ? 'power-off' : 'power-on';

                return (
                    <div className="table-actions">
                        <button
                            disabled={!permisos.includes(20)}
                            className={`action-button power ${powerButtonColorClass}`}
                            title={role.status === 1 ? 'Desactivar' : 'Activar'}
                            onClick={() => handleToggleStatus(role.id)}
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="3"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <circle cx="12" cy="12" r="9" />
                                <line x1="12" y1="4" x2="12" y2="8" />
                            </svg>
                        </button>

                        <button
                            disabled={!permisos.includes(21)}
                            className="action-button edit"
                            title="Editar"
                            onClick={() => handleOpenEditModal(role)}
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="16"
                                height="16"
                                fill="white"
                                viewBox="0 0 24 24"
                            >
                                <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25z" />
                                <path d="M20.71 7.04a1.003 1.003 0 0 0 0-1.41l-2.34-2.34a1.003 1.003 0 0 0-1.41 0L15.13 4.9l3.75 3.75 1.83-1.61z" />
                            </svg>
                        </button>

                        <button
                            disabled={!permisos.includes(22)}
                            className="action-button  view "
                            title="Ver módulos"
                            onClick={() => handleViewModules(role.id)}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" stroke="currentColor"
                                strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                <circle cx="12" cy="12" r="3" />
                            </svg>
                        </button>
                    </div>
                );
            },
        },
        {
            accessorKey: 'name',
            header: 'Rol',
        },
        {
            accessorKey: 'status',
            header: 'Estado',
            cell: ({ getValue }) => {
                const status = getValue<number>();
                const statusText = status === 1 ? 'Activo' : 'Inactivo';
                return (
                    <span className={`status-text ${statusText.toLowerCase()}`}>
                        {statusText}
                    </span>
                );
            },
        },
    ], [permisos, rolePermissions, roles, allModulos]);

    // Crear instancia de la tabla
    const table = useReactTable({
        data: roles,
        columns,
        state: {
            globalFilter,
        },
        onGlobalFilterChange: setGlobalFilter,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
    });

    if (isLoading) {
        return <LoadingSpinner />;
    }

    if (error) {
        return (
            <div className="alert alert-danger">
                {error}
            </div>
        );
    }

    return (
        <div className="roles-container">
            <div className="dashboard-header">
                <div className="roles-header-toolbar">
                    <button
                        disabled={!permisos.includes(19)}
                        className="add-bot-tab-button"
                        onClick={() => setShowNewRoleModal(true)}
                    >
                        Añadir nuevo Role
                    </button>

                    <div className="search-container">
                        <input
                            type="text"
                            placeholder="Buscar rol..."
                            value={globalFilter}
                            onChange={(e) => setGlobalFilter(e.target.value)}
                            className="global-filter"
                        />
                    </div>
                </div>
            </div>

            <div className="roles-table-container">



                {/* Tabla con react-table */}

                <table className="demo-request-table">
  <colgroup>
    <col style={{ width: '25%' }} /><col style={{ width: '50%' }} /><col style={{ width: '25%' }} />
  </colgroup>
                    <thead>
                        {table.getHeaderGroups().map(headerGroup => (
                            <tr key={headerGroup.id}>
                                {headerGroup.headers.map(header => (
                                    <th
                                        key={header.id}
                                        className={header.column.id === 'actions' ? 'center-header' : ''}
                                    >
                                        {flexRender(header.column.columnDef.header, header.getContext())}
                                    </th>
                                ))}
                            </tr>
                        ))}
                    </thead>
                    <tbody>
                        {table.getRowModel().rows.map(row => (
                            <tr key={row.id}>
                                {row.getVisibleCells().map(cell => (
                                    <td
                                        key={cell.id}
                                        className={cell.column.id === 'actions' ? 'center-cell' : 'center-text'}
                                    >
                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>

                {/* Controles de paginación */}
                <div className="pagination-controls">
                    <button
                        onClick={() => table.previousPage()}
                        disabled={!table.getCanPreviousPage()}
                    >
                        ←
                    </button>

                    {getPaginationRange(
                        table.getState().pagination.pageIndex + 1,
                        table.getPageCount()
                    ).map((page, idx) =>
                        page === "..." ? (
                            <span key={idx} className="dots">...</span>
                        ) : (
                            <button
                                key={idx}
                                className={
                                    table.getState().pagination.pageIndex + 1 === page
                                        ? "active-page"
                                        : ""
                                }
                                onClick={() => table.setPageIndex(Number(page) - 1)}
                            >
                                {page}
                            </button>
                        )
                    )}

                    <button
                        onClick={() => table.nextPage()}
                        disabled={!table.getCanNextPage()}
                    >
                        →
                    </button>
                </div>


                {/* Modales */}
                <EditRoleNameModal
                    role={editingRole}
                    show={showEditModal}
                    onClose={handleCloseEditModal}
                    onSave={handleSaveRoleName}
                />

                <ModulesModal
                    show={showModulesModal}
                    onClose={() => setShowModulesModal(false)}
                    modulos={selectedRoleModules}
                    permisos={permisos}
                    roles={rolePermissions}
                    idRol={idRol}
                    onBatchUpdate={handleBatchPermissionUpdate}
                />

                <CreateRoleModal
                    show={showNewRoleModal}
                    onClose={() => setShowNewRoleModal(false)}
                    onSuccess={handleSuccess}
                />
            </div>
        </div>
    );
};

export default Roles;